import React, { useEffect } from 'react'
import ZionForm from '@fs/zion-form'
import { ContentDelete, NoticeLoading } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { Row } from '@fs/zion-ui'
import { trackLink } from '@fs/zion-analytics'
import type { Playlist } from '../../../api/rootstech/playlist/playlist.types'
import PillButton from '../../../lib/buttons/PillButton'
import PlaylistTitleTextField from './PlaylistTitleTextField'
import PlaylistDescriptionTextField from './PlaylistDescriptionTextField'
import usePlaylistForm from '../hooks/usePlaylistForm'

type UpdatePlaylistFormProps = {
  playlist?: Partial<Playlist>
  onSubmit: (data: { name: string; description: string }) => void | Promise<void>
  onDeleteClick?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | null
  onCancelClick?: (() => void) | null
}

export default function UpdatePlaylistForm({
  playlist = {},
  onSubmit,
  onDeleteClick = null,
  onCancelClick = null,
}: UpdatePlaylistFormProps): JSX.Element {
  const [t] = useTranslation()
  const { methods } = usePlaylistForm(playlist)
  useEffect(() => {
    methods.setFocus('name')
  }, [methods])
  const {
    formState: { isSubmitting },
  } = methods
  return (
    // noValidate is a native HTML5 attribute that gets applied to the form. It disables native validation for emails, phone numbers, etc. We're using our own validation here.
    <ZionForm methods={methods} onSubmit={onSubmit} noValidate>
      <PlaylistTitleTextField disabled={isSubmitting} />
      <PlaylistDescriptionTextField disabled={isSubmitting} />
      <Row alignX="end">
        {playlist.id ? (
          <PillButton
            Icon={ContentDelete}
            onClick={(e) => {
              trackLink({ name: `${playlist.name} : Delete playlist` })
              onDeleteClick?.(e)
            }}
            emphasis="low"
            color="red"
            data-testid="delete-playlist"
          >
            {t('common-ui:delete.action', 'Delete')}
          </PillButton>
        ) : null}
        <div growX />
        {onCancelClick && (
          <PillButton emphasis="low" color="blue" onClick={onCancelClick}>
            {t('common-ui:cancel.action', 'Cancel')}
          </PillButton>
        )}
        <PillButton
          emphasis="medium"
          type="submit"
          linkName="Save Edited Playlist"
          data-testid="save-playlist"
          disabled={isSubmitting}
          Icon={isSubmitting ? NoticeLoading : undefined}
        >
          {t('common-ui:save.action', 'Save')}
        </PillButton>
      </Row>
    </ZionForm>
  )
}
